<div class="flex flex-col gap-3" role="status" aria-live="polite">

    <!-- ── Boot-Slot: genau ein Boot je Buchung ── -->
    @if (boat(); as b) {
        <div class="rounded-lg border border-p-border overflow-hidden">
            <div class="flex items-center gap-3 px-6 py-3 bg-[color-mix(in_srgb,#0284c7_8%,transparent)]">
                <span class="material-icons flex-shrink-0 text-sky-600" aria-hidden="true">directions_boat</span>
                <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2 flex-wrap">
                        <span class="font-semibold text-p-text">
                            @if (b.origin === 'manual') {
                                {{ 'bookingV2.berth.summary.manualBoat' | translate }}
                            } @else {
                                {{ b.name || ('bookingV2.berth.summary.unnamedBoat' | translate) }}
                            }
                        </span>
                        @if (b.licensePlate) {
                            <span class="text-sm text-p-text-muted">({{ b.licensePlate }})</span>
                        }
                        @if (b.origin === 'new') {
                            <span class="text-xs font-medium px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                                {{ 'bookingV2.berth.summary.newBadge' | translate }}
                            </span>
                        } @else if (b.origin === 'manual') {
                            <span class="text-xs font-medium px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-700">
                                {{ 'bookingV2.berth.summary.manualBadge' | translate }}
                            </span>
                        }
                    </div>
                    <div class="text-sm text-p-text-muted mt-0.5">
                        <booking-v2-dimensions [length]="b.length" [width]="b.width" [depth]="b.depth" />
                        @if (b.ownerLabel) {
                            · {{ 'bookingV2.berth.summary.ownerLabel' | translate }}: {{ b.ownerLabel }}
                        }
                    </div>
                    @if (searchDimensions(); as s) {
                        <div class="flex items-center gap-1 text-sm text-p-text-muted mt-0.5">
                            <span class="material-icons text-base" aria-hidden="true">straighten</span>
                            <span>
                                {{ 'bookingV2.berth.summary.searchDimensions' | translate }}:
                                <booking-v2-dimensions [length]="s.length" [width]="s.width" [depth]="s.depth" />
                            </span>
                        </div>
                    }
                </div>
                <u2b-icon-button
                    icon="delete_outline"
                    [ariaLabel]="('bookingV2.berth.summary.removeBoatAria' | translate)"
                    [tooltip]="('bookingV2.berth.summary.removeBoatHelp' | translate)"
                    tooltipPosition="top"
                    (clicked)="changeBoatRequested.emit()" />
            </div>
        </div>
    } @else {
        <div class="flex items-center gap-3 px-6 py-3 rounded-lg border border-dashed border-p-border">
            <span class="material-icons flex-shrink-0 text-p-text-muted" aria-hidden="true">directions_boat</span>
            <div class="flex-1 min-w-0">
                <div class="font-semibold text-p-text">{{ 'bookingV2.berth.slots.boatEmptyTitle' | translate }}</div>
                <div class="text-sm text-p-text-muted mt-0.5">{{ 'bookingV2.berth.slots.boatEmptyHint' | translate }}</div>
            </div>
        </div>
    }

    <!-- ── Liegeplatz-Slot: mehrere Belegungen möglich, je expandierbar (6609) ── -->
    @if (occupancies().length > 0) {
        <div class="rounded-lg border border-p-border overflow-hidden">
            <div class="flex flex-col divide-y divide-p-border">
                @for (occ of occupancies(); track occ.uuid) {
                    @if (occ.berth; as berth) {
                        <div class="flex flex-col bg-[color-mix(in_srgb,#0f766e_8%,transparent)]">

                            <!-- Collapsed header: berth + window summary -->
                            <div class="flex items-center gap-3 px-6 py-3">
                                <span class="material-icons flex-shrink-0 text-teal-700" aria-hidden="true">anchor</span>
                                <div class="flex-1 min-w-0">
                                    <div class="flex items-center gap-2 flex-wrap">
                                        <span class="font-semibold text-p-text">{{ berth.handle }}</span>
                                        @if (!occ.overridden) {
                                            <span class="text-xs font-medium px-1.5 py-0.5 rounded-full bg-teal-100 text-teal-700">
                                                {{ 'bookingV2.berth.occupancy.defaultBadge' | translate }}
                                            </span>
                                        }
                                    </div>
                                    <div class="text-sm text-p-text-muted mt-0.5 flex items-center gap-1 flex-wrap">
                                        @if (berth.pierHandle) {
                                            <span>{{ berth.pierHandle }} ·</span>
                                        }
                                        <span class="material-icons text-sm" aria-hidden="true">event</span>
                                        <span>
                                            {{ formatDate(occ.from) }}
                                            {{ 'bookingV2.dateRange.until' | translate }}
                                            @if (occ.openEnd) {
                                                {{ 'bookingV2.dateRange.openEndShort' | translate }}
                                            } @else {
                                                {{ formatDate(occ.to) }}
                                            }
                                        </span>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    class="bg-transparent hover:bg-[color-mix(in_srgb,#0f766e_14%,transparent)] text-teal-700 hover:text-teal-900 rounded p-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-p-primary"
                                    [attr.aria-expanded]="isExpanded(occ.uuid)"
                                    [attr.aria-label]="((isExpanded(occ.uuid) ? 'bookingV2.berth.occupancy.collapseAria' : 'bookingV2.berth.occupancy.expandAria') | translate)"
                                    (click)="toggleExpanded(occ.uuid)">
                                    <span
                                        class="material-icons transition-transform"
                                        [style.transform]="isExpanded(occ.uuid) ? 'rotate(180deg)' : 'rotate(0)'"
                                        aria-hidden="true">expand_more</span>
                                </button>
                                <u2b-icon-button
                                    icon="delete_outline"
                                    [ariaLabel]="('bookingV2.berth.occupancy.removeAria' | translate)"
                                    (clicked)="removeOccupancy(occ.uuid)" />
                            </div>

                            <!-- Expanded: editable Belegungszeitfenster -->
                            @if (isExpanded(occ.uuid)) {
                                <div class="px-6 pt-2 pb-5 flex flex-col gap-3 border-t border-[color-mix(in_srgb,#0f766e_18%,transparent)]">
                                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <div class="flex flex-col gap-1">
                                            <label class="text-sm font-medium text-p-text">
                                                {{ 'bookingV2.dateRange.fromLabel' | translate }}
                                            </label>
                                            <p-datepicker
                                                u2bDateMask [u2bDateMaskTime]="true"
                                                u2bDatePickerApply #applyOccFrom="u2bDatePickerApply"
                                                [pt]="customTimePt"
                                                [u2bDatePickerFallbackTime]="checkInFallbackTime()"
                                                dateFormat="dd.mm.yy"
                                                [showTime]="true"
                                                hourFormat="24"
                                                [showIcon]="true"
                                                [readonlyInput]="false"
                                                [showButtonBar]="true"
                                                appendTo="body"
                                                styleClass="w-full"
                                                [placeholder]="('bookingV2.dateRange.datetimePlaceholder' | translate)"
                                                [ngModel]="occ.from"
                                                (ngModelChange)="onFromChange(occ.uuid, $event)">
                                                <ng-template #triggericon><span class="material-icons">calendar_today</span></ng-template>
                                                <ng-template #previousicon><span class="material-icons">chevron_left</span></ng-template>
                                                <ng-template #nexticon><span class="material-icons">chevron_right</span></ng-template>
                                                <ng-template #buttonbar>
                                                    <u2b-date-picker-button-bar
                                                        [picker]="applyOccFrom"/>
                                                </ng-template>
                                            </p-datepicker>
                                        </div>
                                        <div class="flex flex-col gap-1">
                                            <label class="text-sm font-medium text-p-text">
                                                {{ 'bookingV2.dateRange.toLabel' | translate }}
                                            </label>
                                            @if (occ.openEnd) {
                                                <p-datepicker
                                                    class="u2b-open-end-field"
                                                    [disabled]="true"
                                                    [showIcon]="true"
                                                    styleClass="w-full"
                                                    [placeholder]="('bookingV2.dateRange.openEndShort' | translate)">
                                                    <ng-template #triggericon><span class="material-icons">all_inclusive</span></ng-template>
                                                </p-datepicker>
                                            } @else {
                                                <p-datepicker
                                                    u2bDateMask [u2bDateMaskTime]="true"
                                                    u2bDatePickerApply #applyOccTo="u2bDatePickerApply"
                                                    [pt]="customTimePt"
                                                    [u2bDatePickerFallbackTime]="checkOutFallbackTime()"
                                                    dateFormat="dd.mm.yy"
                                                    [showTime]="true"
                                                    hourFormat="24"
                                                    [showIcon]="true"
                                                    [readonlyInput]="false"
                                                    [showButtonBar]="true"
                                                    appendTo="body"
                                                    styleClass="w-full"
                                                    [minDate]="occ.from ?? undefined"
                                                    [placeholder]="('bookingV2.dateRange.datetimePlaceholder' | translate)"
                                                    [ngModel]="occ.to"
                                                    (ngModelChange)="onToChange(occ.uuid, $event)">
                                                    <ng-template #triggericon><span class="material-icons">calendar_today</span></ng-template>
                                                    <ng-template #previousicon><span class="material-icons">chevron_left</span></ng-template>
                                                    <ng-template #nexticon><span class="material-icons">chevron_right</span></ng-template>
                                                    <ng-template #buttonbar>
                                                        <u2b-date-picker-button-bar
                                                            [picker]="applyOccTo"/>
                                                    </ng-template>
                                                </p-datepicker>
                                            }
                                        </div>
                                    </div>

                                    <div class="flex items-center justify-between gap-3">
                                        <span class="text-sm text-p-text-muted">
                                            {{ 'bookingV2.dateRange.openEnd' | translate }}
                                        </span>
                                        <p-toggleswitch
                                            [ngModel]="occ.openEnd"
                                            (onChange)="onOpenEndChange(occ.uuid, $any($event.checked))" />
                                    </div>

                                    @if (occ.overridden) {
                                        <button
                                            type="button"
                                            class="self-start inline-flex items-center gap-1 text-sm font-medium text-p-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-p-primary rounded"
                                            (click)="resetToDefault(occ.uuid)">
                                            <span class="material-icons text-base" aria-hidden="true">restart_alt</span>
                                            {{ 'bookingV2.berth.occupancy.resetToDefault' | translate }}
                                        </button>
                                    }
                                </div>
                            }
                        </div>
                    }
                }
            </div>
        </div>
    } @else {
        <div class="flex items-center gap-3 px-6 py-3 rounded-lg border border-dashed border-p-border">
            <span class="material-icons flex-shrink-0 text-p-text-muted" aria-hidden="true">anchor</span>
            <div class="flex-1 min-w-0">
                <div class="font-semibold text-p-text">{{ 'bookingV2.berth.slots.berthEmptyTitle' | translate }}</div>
                <div class="text-sm text-p-text-muted mt-0.5">{{ 'bookingV2.berth.slots.berthEmptyHint' | translate }}</div>
            </div>
        </div>
    }

</div>
